import type { LocationResource } from '@provider-directory/types/locations.contracts';
import { CreateLocationLabelOptions, createLocationLabel } from './string';

const DEFAULT_GEOLOCATION_OPTIONS = {
  timeout: 25 * 1000,
};
interface SuccessUserGeolocation {
  status: 'SUCCESS';
  latitude: number;
  longitude: number;
}

interface ErrorUserGeolocation {
  status: 'ERROR';
}

export type ClientGeolocation = SuccessUserGeolocation | ErrorUserGeolocation;

const mapSuccess = (location: GeolocationPosition): SuccessUserGeolocation => ({
  status: 'SUCCESS',
  latitude: location.coords.latitude,
  longitude: location.coords.longitude,
});

const mapError = (error: GeolocationPositionError): ErrorUserGeolocation => {
  // Switch statement is for the case when we will need to show specific location error
  switch (error.code) {
    case 0: // unknown error
    case 1: // permission denied
    case 2: // position unavailable
    case 3: // timeout
    default:
      return {
        status: 'ERROR',
      };
  }
};

export const getUserLocation = async (): Promise<ClientGeolocation> => {
  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          DEFAULT_GEOLOCATION_OPTIONS,
        );
      },
    );
    return mapSuccess(position);
  } catch (error) {
    return mapError(error as GeolocationPositionError);
  }
};

export const isGeolocationSuccess = (
  location: ClientGeolocation,
): location is SuccessUserGeolocation => location.status === 'SUCCESS';

export const getMapsLinkByName = (location: LocationResource): string => {
  const { street, city, state, zip } = location.address;

  const destination = encodeURIComponent(
    `${location.name}, ${street}, ${city}, ${state} ${zip}`,
  );
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

export const getLocationPhone = (
  location: LocationResource | null | undefined,
): string | null => {
  if (!location) return null;

  return (
    location.contacts.filter((contact) => contact.type === 'phone')[0]?.value ??
    null
  );
};

/**
 * Returns displayName and addressLabel for a location, handling the no-name case.
 * - If location.name is missing/empty, use street as displayName and omit street from addressLabel.
 */
export function getLocationDisplayInfo(
  location: LocationResource,
  options?: CreateLocationLabelOptions,
): {
  displayName: string;
  addressLabel: string;
} {
  let displayName = location.name || '';
  let addressLabel = createLocationLabel(location.address, options);

  if (!location.name || location.name.trim().length === 0) {
    displayName = location.address.street;
    addressLabel = createLocationLabel(
      { ...location.address, street: '' },
      options,
    ).replace(/^,\s*/, '');
  }

  return { displayName, addressLabel };
}
